import {
  getFileExtension,
  getFileTypeFromUri,
  MimeTypes,
  setFileExtensionToLowercase,
} from "#/lib/string";
import { ApiEndpoints } from "./apiEndpoints";
import { fetchData, setFileUploadOptions } from "./apiHelpers";
import {
  AdditionalUploadData,
  UploadAsset,
  UploadMultipleFilesResponse,
} from "./storage.type";

export async function uploadFile({
  assets,
  additionalData,
}: {
  assets: UploadAsset[];
  additionalData?: AdditionalUploadData;
}): Promise<UploadMultipleFilesResponse> {
  try {
    const slug = "/storage/multiple";
    const formData: FormData = new FormData();

    assets.forEach((asset, index) => {
      // Determine file type based on asset.type or by checking file extension
      const fileType =
        (asset.mimeType as MimeTypes | undefined) ??
        getFileTypeFromUri(asset.uri);

      // Use the original filename if available, otherwise use the provided name with index
      const fileName =
        asset.fileName ||
        `${index}${getFileExtension(asset.uri, fileType as MimeTypes)}`;

      const lowerCaseFileName = setFileExtensionToLowercase(fileName);

      const fileValue = asset.file
        ? asset.file
        : ({ uri: asset.uri, type: fileType, name: lowerCaseFileName } as any);

      // When a File/Blob is available (web uploads) the browser handles metadata
      // automatically. For fallback objects (native/mobile) keep the previous shape.
      if (asset.file && asset.file instanceof File) {
        formData.append("file", asset.file, lowerCaseFileName);
      } else if (asset.file && asset.file instanceof Blob) {
        formData.append("file", asset.file, lowerCaseFileName);
      } else {
        formData.append("file", fileValue);
      }
    });

    if (additionalData) {
      for (const key in additionalData) {
        const value = additionalData[key as keyof AdditionalUploadData];
        if (typeof value === "string" && value.length > 0) {
          formData.append(key, value);
        }
      }
    }

    const request = await setFileUploadOptions({
      method: "POST",
      body: formData,
    });

    const response = await fetchData<UploadMultipleFilesResponse>(
      slug,
      request
    );

    return response;
  } catch (error) {
    console.error("Error uploading file", error);
    throw error;
  }
}

export async function uploadAudioFile({
  uri,
  fileName,
  additionalData,
  mimeType = "audio/mpeg",
}: {
  uri: string;
  fileName: string;
  additionalData: {
    ambientConversationId: string;
    purpose: string;
  };
  mimeType?: string;
}) {
  return uploadMultipleAudioFiles(
    [{ uri, fileName, mimeType }],
    additionalData
  );
}

export async function uploadMultipleAudioFiles(
  files: Array<{
    uri: string;
    fileName: string;
    mimeType?: string;
  }>,
  additionalData?: {
    ambientConversationId: string;
  }
): Promise<UploadMultipleFilesResponse> {
  try {
    const slug = ApiEndpoints.storage + "/audio";
    const formData = new FormData();

    files.forEach((file) => {
      const mimeType = file.mimeType || "audio/mpeg";
      formData.append("file", {
        uri: file.uri,
        type: mimeType,
        name: file.fileName,
      } as any);
    });

    if (additionalData) {
      for (const key in additionalData) {
        formData.append(
          key,
          additionalData[key as keyof typeof additionalData]
        );
      }
    }

    const request = await setFileUploadOptions({
      method: "POST",
      body: formData,
    });

    return fetchData<UploadMultipleFilesResponse>(slug, request);
  } catch (error) {
    console.error("Error uploading audio files", error);
    throw error;
  }
}
